import React, { useState } from 'react'
import ExpandableTable from '../common/components/ExpandableTable'
import Form from 'react-bootstrap/Form'
import CopyButtonInBox from '../common/components/CopyButtonInBox.jsx'
import FileContents from './FileContents'
import { fileDownload } from '../common/fileDownload'
import { rewriteClientUrl } from '../common/constants'

// display a list of details from the s3 list of client interaction object's keys plus lastModified date-time
export default function History({
  columns,
  data,
  maxHistoryHeight,
  getInteraction,
  setInteractionLoading
}) {
  const [expandedRowIds, setExpandedRowIds] = useState(new Set())
  const [cachedContents, setCachedContents] = useState({})
  const [showModal, setShowModal] = useState(false)
  const [modalContent, setModalContent] = useState('')

  const LinkifiedText = ({ text }) => {
    const urlRegex = /(https:\/\/[^\s"]+)/

    // handle null/undefined and non-string values safely
    if (
      text === null ||
      text === undefined ||
      typeof text !== 'string' ||
      !urlRegex.test(text)
    ) {
      const displayText =
        text === null || text === undefined
          ? ''
          : typeof text === 'string'
            ? text
            : JSON.stringify(text, null, 2)
      return (
        <div className="copy-button-container">
          <CopyButtonInBox textToCopy={displayText} disableTooltip={true} />
          <Form.Control
            className={'styled-text-input'}
            as="textarea"
            readOnly
            value={displayText}
            rows={10}
            style={{ fontFamily: 'monospace' }}
          />
        </div>
      )
    }

    const parts = text.split(urlRegex)

    return (
      <div
       className="copy-button-container"
       style={{
          whiteSpace: 'pre-wrap',
          fontFamily: 'monospace',
          border: '1px solid #ced4da',
          borderRadius: '.25rem',
          padding: '.375rem .75rem',
          background: '#e9ecef',
          opacity: 1,
          position: 'relative'
        }}
      >
        <CopyButtonInBox textToCopy={text} />
        {parts.map((part, i) => {
          if (part.match(urlRegex)) {
            const href = rewriteClientUrl(part)
            return (
              <a
                key={`${i}-${part.substring(0, 20)}`}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
              >
                {href}
              </a>
            )
          }
          return part
        })}
      </div>
    )
  }

  const onToggleExpand = async (key) => {
    if (!key) return
    setInteractionLoading(true)
    try {
      const currentExpanded = expandedRowIds.has(key)
      const newExpanded = new Set(expandedRowIds)

      if (currentExpanded) {
        newExpanded.delete(key)
      } else {
        if (!cachedContents[key]) {
          const result = await getInteraction(key)
          // store raw result; LinkifiedText will stringify if needed
          setCachedContents((prev) => ({ ...prev, [key]: result }))
        }
        newExpanded.add(key)
      }
      setExpandedRowIds(newExpanded)
    } finally {
      setInteractionLoading(false)
    }
  }

  const onShowInteraction = async (row) => {
    const key = row.original?.transaction_key
    if (!key) return

    setInteractionLoading(true)
    try {
      let body = cachedContents[key]
      if (!body) {
        body = await getInteraction(key)
        setCachedContents((prev) => ({ ...prev, [key]: body }))
      }
      setModalContent(body)
      setShowModal(true)
    } finally {
      setInteractionLoading(false)
    }
  }

  const onDownloadInteraction = async (row) => {
    const key = row.original?.transaction_key
    if (!key) return

    setInteractionLoading(true)
    try {
      let body = cachedContents[key]
      if (!body) {
        body = await getInteraction(key)
        setCachedContents((prev) => ({ ...prev, [key]: body }))
      }
      const blob = new Blob([
        typeof body === 'string' ? body : JSON.stringify(body, null, 2),
      ], { type: 'application/json;charset=utf-8' })
      const filename = `${key}.txt`
      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(blob, filename)
      } else {
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
      }
    } finally {
      setInteractionLoading(false)
    }
  }

  const onCopyInteraction = async (row) => {
    const key = row.original?.transaction_key
    if (!key) return

    setInteractionLoading(true)
    try {
      let body = cachedContents[key]
      if (!body) {
        body = await getInteraction(key)
        setCachedContents((prev) => ({ ...prev, [key]: body }))
      }
      const textToCopy =
        typeof body === 'string' ? body : JSON.stringify(body, null, 2)
      await navigator.clipboard.writeText(textToCopy)
    } finally {
      setInteractionLoading(false)
    }
  }

  const hasCachedBody = (row) => {
    const key = row.original?.transaction_key
    if (!key) return false
    return Object.prototype.hasOwnProperty.call(cachedContents, key)
  }

  const getPreviewText = async (row) => {
    const key = row.original?.transaction_key
    if (!key) return ''

    let body = cachedContents[key]
    if (!body) {
      // Fetch on hover and cache the result for reuse by other actions
      const fetched = await getInteraction(key)
      setCachedContents((prev) => ({ ...prev, [key]: fetched }))
      body = fetched
    }
    let parts = []
    const text = typeof body === 'string' ? body : JSON.stringify(body, null, 2)

    const xmlTagRegexes = [
      /RspCode/,
      /RspMessage/,
      /Code/,
      /Description/,
      /sr:ResponseCode/,
      /CRQ\d{4}[A-Za-z]{0,200}Inputs/,
      /ra:GBCSHexAlertCode/,
      /ra:AlertDescription/,
      /StatusCode/,
      /Warning/,
    ]
    for (const tagRe of xmlTagRegexes) {
      const m = text.match(RegExp(`<${tagRe.source}>([\\s\\S]{0,1000})<\/${tagRe.source}>`));
      if (m) parts.push(m[1]); // inner text
    }

    const jsonTagRegexes = [
      /rsp_code/,
      /rsp_message/,
      /code/,
      /description/,
      /status_code/,
      /warning/,
    ]

    for (const tagRe of jsonTagRegexes) {
      const m = text.match(RegExp(`"${tagRe.source}"\\s?\:\\s?"([^"]{0,1000})`));
      if (m) parts.push(m[1]); // inner text
    }

    const trimmed = parts.join(" - ").trim()

    return trimmed.length === 0 ? 'N/A' : trimmed.length > 140 ? trimmed.slice(0, 140) + '…' : trimmed
  }

  const renderRowSubComponent = ({ row }) => {
    const key = row.original?.transaction_key
    const body = cachedContents[key] || 'Loading...'
    return <LinkifiedText text={body} />
  }

  // allow the table to scroll and only take up 40% of the view port
  return (
    <div
      style={{
        overflow: 'auto',
        maxHeight: maxHistoryHeight - 35,
        position: 'relative',
      }}
    >
      <ExpandableTable
        columns={columns}
        data={data}
        onToggleExpand={onToggleExpand}
        expandedRowIds={expandedRowIds}
        renderRowSubComponent={renderRowSubComponent}
        showColumn={{
          onClick: onShowInteraction,
          onDownload: onDownloadInteraction,
          onCopy: onCopyInteraction,
          getPreview: getPreviewText,
          hasCachedBody,
        }}
      />
      <FileContents
        fileContents={
          typeof modalContent === 'string'
            ? modalContent
            : JSON.stringify(modalContent, null, 2)
        }
        show={showModal}
        onHide={() => setShowModal(false)}
      />
    </div>
  )
}
